import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Badge,
  Card,
  Form,
  InputGroup,
  Modal,
  Alert,
} from "react-bootstrap";
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaCheck,
  FaBan,
  FaRegClipboard,
  FaEye,
} from "react-icons/fa";
import axios from "axios";
import { format } from "date-fns";
import { useAuth } from "../context/AuthContext";

const DEFAULT_LICENSE_FEATURES = {
  allow_builder_deploy: false,
  allow_tire_projects: false,
  is_test_license: false,
};

const COLUMN_OPTIONS = [
  { key: "customer_name", label: "Name" },
  { key: "first_name", label: "Vorname" },
  { key: "last_name", label: "Nachname" },
  { key: "email", label: "E-Mail-Adresse" },
  { key: "company", label: "Firma" },
  { key: "created_at", label: "Created" },
  { key: "created_by_email", label: "Created By" },
  { key: "expires_at", label: "Expires" },
  { key: "activations", label: "Activations" },
  { key: "features", label: "Features" },
];

const DEFAULT_VISIBLE_COLUMNS = [
  "customer_name",
  "company",
  "expires_at",
  "activations",
  "features",
];

const LICENSE_KIND_OPTIONS = [
  { value: "all", label: "All Licenses" },
  { value: "normal", label: "Normal Licenses" },
  { value: "test", label: "Test Licenses" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "verified", label: "Verified" },
  { value: "pending", label: "Pending" },
  { value: "revoked", label: "Revoked" },
  { value: "suspended", label: "Suspended" },
  { value: "expired", label: "Expired" },
];

const withFeatureDefaults = (license = {}) => {
  const rawFeatures =
    license && typeof license.features === "object" ? license.features : {};
  const mergedFeatures = {
    ...DEFAULT_LICENSE_FEATURES,
    ...rawFeatures,
  };

  return {
    ...license,
    features: {
      ...mergedFeatures,
      allow_builder_deploy: Boolean(
        mergedFeatures.allow_builder_deploy ??
        DEFAULT_LICENSE_FEATURES.allow_builder_deploy
      ),
      allow_tire_projects: Boolean(
        mergedFeatures.allow_tire_projects ??
        DEFAULT_LICENSE_FEATURES.allow_tire_projects
      ),
      is_test_license: Boolean(
        mergedFeatures.is_test_license ??
        DEFAULT_LICENSE_FEATURES.is_test_license
      ),
    },
  };
};

const safeString = (value) => (value === undefined || value === null ? "" : String(value));

const getDisplayName = (license = {}) =>
  license.user_name || license.legacy_name || license.name || "Unknown";

const Licenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revealedKeys, setRevealedKeys] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLicense, setEditLicense] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState(null);
  const { logout } = useAuth();

  useEffect(() => {
    // Fetch licenses
    const fetchLicenses = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.get("/api/licenses");
        const data = Array.isArray(response.data) ? response.data : [];
        setLicenses(data.map(withFeatureDefaults));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching licenses:", error);
        if (error.response && error.response.status === 401) {
          setError("Your session has expired. Please log in again.");
          // The axios interceptor will handle the logout
        } else {
          setError("Failed to fetch licenses. Please try again later.");
        }
        setLoading(false);
      }
    };

    fetchLicenses();
  }, []);

  const handleToggleLicenseKey = (licenseKey) => {
    setRevealedKeys((prev) => ({
      ...prev,
      [licenseKey]: !prev[licenseKey],
    }));
  };

  const handleCopyLicenseKey = async (licenseKey) => {
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(licenseKey);
        return;
      } catch (copyError) {
        console.error("Failed to copy license key:", copyError);
      }
    }
    // Fallback prompt if Clipboard API unavailable or failed
    window.prompt("Copy license key:", licenseKey);
  };

  const handleShowDetails = (license) => {
    setSelectedLicense(withFeatureDefaults(license));
    setShowDetailsModal(true);
  };

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prevColumns) =>
      prevColumns.includes(columnKey)
        ? prevColumns.filter((key) => key !== columnKey)
        : [...prevColumns, columnKey]
    );
  };

  const getStatusBadge = (status, expiryDate) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const hasExpired =
      expiryDate && !Number.isNaN(expiry.getTime()) && expiry <= now;

    if ((status === "active" || status === "verified") && hasExpired) {
      return <Badge bg="warning">Expired</Badge>;
    } else if (status === "active") {
      return <Badge bg="success">Active</Badge>;
    } else if (status === "verified") {
      return <Badge bg="primary">Verified</Badge>;
    } else if (status === "revoked") {
      return <Badge bg="danger">Revoked</Badge>;
    } else {
      return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleRevokeLicense = async (licenseKey) => {
    if (!window.confirm("Are you sure you want to revoke this license?")) {
      return;
    }

    try {
      await axios.delete(`/api/licenses/${licenseKey}`);
      // Update local state
      setLicenses((prevLicenses) =>
        prevLicenses.map((license) =>
          license.license_key === licenseKey
            ? { ...license, status: "revoked" }
            : license
        )
      );
      console.log(`License ${licenseKey} successfully revoked`);
    } catch (error) {
      console.error("Error revoking license:", error);
      alert("Failed to revoke license. Please try again.");
    }
  };

  const handleRestoreLicense = async (licenseKey) => {
    if (!window.confirm("Are you sure you want to restore this license?")) {
      return;
    }

    try {
      await axios.put(`/api/licenses/${licenseKey}`, { status: "active" });

      // Update local state
      setLicenses((prevLicenses) =>
        prevLicenses.map((license) =>
          license.license_key === licenseKey
            ? { ...license, status: "active" }
            : license
        )
      );
      console.log(`License ${licenseKey} successfully restored`);
    } catch (error) {
      console.error("Error restoring license:", error);
      alert("Failed to restore license. Please try again.");
    }
  };

  const handleEditLicense = (license) => {
    setEditLicense(withFeatureDefaults(license));
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(
        `/api/licenses/${editLicense.license_key}`,
        editLicense
      );
      const updatedLicense = withFeatureDefaults(response.data);

      // Update local state
      setLicenses((prevLicenses) =>
        prevLicenses.map((license) =>
          license.license_key === editLicense.license_key
            ? updatedLicense
            : license
        )
      );
      setEditLicense(updatedLicense);
      setShowEditModal(false);
      console.log(`License ${editLicense.license_key} successfully updated`);
    } catch (error) {
      console.error("Error updating license:", error);
      alert("Failed to update license. Please try again.");
    }
  };

  const handleDeleteLicense = (license) => {
    setLicenseToDelete(license);
    setShowDeleteModal(true);
  };

  const confirmDeleteLicense = async () => {
    try {
      // Make a custom DELETE request with a special query parameter to indicate
      // permanent deletion instead of just revocation
      await axios.delete(
        `/api/licenses/${licenseToDelete.license_key}?permanent=true`
      );

      // Remove the license from the local state
      setLicenses((prevLicenses) =>
        prevLicenses.filter(
          (license) => license.license_key !== licenseToDelete.license_key
        )
      );

      setShowDeleteModal(false);
      console.log(`License ${licenseToDelete.license_key} permanently deleted`);
    } catch (error) {
      console.error("Error deleting license:", error);
      alert("Failed to delete license. Please try again.");
    }
  };

  const getEffectiveStatus = (license) => {
    const status = safeString(license.status).toLowerCase();
    const expiry = new Date(license.expires_at);
    if (
      (status === "active" || status === "verified") &&
      license.expires_at &&
      !Number.isNaN(expiry.getTime()) &&
      expiry <= new Date()
    ) {
      return "expired";
    }
    return status || "unknown";
  };

  const filteredLicenses = licenses.filter((license) => {
    const normalizedLicense = withFeatureDefaults(license);
    const isTestLicense = normalizedLicense.features.is_test_license;
    const matchesKind =
      kindFilter === "all" ||
      (kindFilter === "test" && isTestLicense) ||
      (kindFilter === "normal" && !isTestLicense);

    const matchesStatus =
      statusFilter === "all" || getEffectiveStatus(license) === statusFilter;

    if (!matchesKind || !matchesStatus) {
      return false;
    }

    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      safeString(license.license_key).toLowerCase().includes(searchLower) ||
      safeString(license.user_id).toLowerCase().includes(searchLower) ||
      safeString(license.user_name).toLowerCase().includes(searchLower) ||
      safeString(license.first_name).toLowerCase().includes(searchLower) ||
      safeString(license.last_name).toLowerCase().includes(searchLower) ||
      safeString(license.email).toLowerCase().includes(searchLower) ||
      safeString(license.company).toLowerCase().includes(searchLower)
    );
  });

  const formatEmail = (email) => {
    if (!email) {
      return "Unknown";
    }
    const [local, domain] = email.split("@");
    if (!domain) {
      return email;
    }
    return `${local}@…`;
  };

  const getFeatureBadges = (features = {}) => {
    const normalizedFeatures = {
      ...DEFAULT_LICENSE_FEATURES,
      ...features,
    };

    return (
      <>
        {normalizedFeatures.is_test_license && (
          <Badge bg="info" className="me-1">
            Test
          </Badge>
        )}
        {normalizedFeatures.allow_builder_deploy && (
          <Badge bg="primary" className="me-1">
            Builder Deploy
          </Badge>
        )}
        {normalizedFeatures.allow_tire_projects && (
          <Badge bg="warning" text="dark" className="me-1">
            Tire Projects
          </Badge>
        )}
        {!normalizedFeatures.is_test_license &&
          !normalizedFeatures.allow_builder_deploy &&
          !normalizedFeatures.allow_tire_projects && (
            <Badge bg="secondary">None</Badge>
          )}
      </>
    );
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "Unknown";
    }
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return "Invalid date";
    }
    return format(parsedDate, "MMM d, yyyy");
  };

  const getActivationText = (license) => {
    return `${license.current_activations !== undefined ? license.current_activations : 0
      }/${license.max_activations || 1}`;
  };

  const getColumnValue = (license, columnKey) => {
    switch (columnKey) {
      case "customer_name":
        return getDisplayName(license);
      case "first_name":
        return license.first_name || "Unknown";
      case "last_name":
        return license.last_name || "Unknown";
      case "email":
        return license.email || "Unknown";
      case "company":
        return license.company || "Unknown";
      case "created_at":
        return formatDate(license.created_at);
      case "created_by_email":
        return formatEmail(license.created_by_email);
      case "expires_at":
        return formatDate(license.expires_at);
      case "activations":
        return getActivationText(license);
      case "features":
        return getFeatureBadges(license.features);
      default:
        return "";
    }
  };

  const renderDetailRow = (label, value) => (
    <tr>
      <th style={{ width: "220px" }}>{label}</th>
      <td>{value || "Unknown"}</td>
    </tr>
  );

  return (
    <Container>
      <h2 className="my-4">License Management</h2>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex flex-column flex-lg-row gap-3 mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by license key, name, email or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Form.Select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              aria-label="Filter by license kind"
              style={{ maxWidth: "220px" }}
            >
              {LICENSE_KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by license status"
              style={{ maxWidth: "220px" }}
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div>
            <div className="text-muted small mb-2">Visible table columns</div>
            <div className="d-flex flex-wrap gap-3">
              {COLUMN_OPTIONS.map((column) => (
                <Form.Check
                  key={column.key}
                  type="checkbox"
                  id={`license-column-${column.key}`}
                  label={column.label}
                  checked={visibleColumns.includes(column.key)}
                  onChange={() => handleToggleColumn(column.key)}
                />
              ))}
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {!loading && (
            <div className="text-muted small mb-2">
              Showing {filteredLicenses.length} of {licenses.length} licenses
            </div>
          )}
          {loading ? (
            <p className="text-center">Loading licenses...</p>
          ) : filteredLicenses.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>License Key</th>
                  {COLUMN_OPTIONS.filter((column) =>
                    visibleColumns.includes(column.key)
                  ).map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLicenses.map((license, index) => (
                  <tr
                    key={license._id || license.license_key || index}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleShowDetails(license)}
                  >
                    <td>
                      <InputGroup size="sm" style={{ maxWidth: "260px" }}>
                        <Form.Control
                          type="text"
                          readOnly
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowDetails(license);
                          }}
                          value={
                            revealedKeys[license.license_key]
                              ? license.license_key || "Unknown"
                              : "••••••••••"
                          }
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleLicenseKey(license.license_key);
                          }}
                        >
                          {revealedKeys[license.license_key] ? "Hide" : "Show"}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyLicenseKey(license.license_key);
                          }}
                          title="Copy license key"
                        >
                          <FaRegClipboard />
                        </Button>
                      </InputGroup>
                    </td>
                    {COLUMN_OPTIONS.filter((column) =>
                      visibleColumns.includes(column.key)
                    ).map((column) => (
                      <td key={column.key}>
                        {getColumnValue(license, column.key)}
                      </td>
                    ))}
                    <td>
                      {getStatusBadge(license.status, license.expires_at)}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        title="View Details"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowDetails(license);
                        }}
                      >
                        <FaEye />
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        title="Edit License"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditLicense(license);
                        }}
                      >
                        <FaEdit />
                      </Button>

                      {license.status !== "revoked" ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="me-2"
                          title="Revoke License"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRevokeLicense(license.license_key);
                          }}
                        >
                          <FaBan />
                        </Button>
                      ) : (
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          title="Restore License"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestoreLicense(license.license_key);
                          }}
                        >
                          <FaCheck />
                        </Button>
                      )}

                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Delete License"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLicense(license);
                        }}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-center">No licenses found.</p>
          )}
        </Card.Body>
      </Card>

      {/* License Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>License Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLicense && (
            <Table responsive bordered size="sm">
              <tbody>
                {renderDetailRow(
                  "License Key",
                  <code>{selectedLicense.license_key}</code>
                )}
                {renderDetailRow("Name", getDisplayName(selectedLicense))}
                {renderDetailRow("Vorname", selectedLicense.first_name)}
                {renderDetailRow("Nachname", selectedLicense.last_name)}
                {renderDetailRow("E-Mail-Adresse", selectedLicense.email)}
                {renderDetailRow("Firma", selectedLicense.company)}
                {renderDetailRow("Product", selectedLicense.product)}
                {renderDetailRow("Type", selectedLicense.type)}
                {renderDetailRow(
                  "Status",
                  getStatusBadge(selectedLicense.status, selectedLicense.expires_at)
                )}
                {renderDetailRow("Created", formatDate(selectedLicense.created_at))}
                {renderDetailRow("Created By", selectedLicense.created_by_email)}
                {renderDetailRow("Expires", formatDate(selectedLicense.expires_at))}
                {renderDetailRow("Activations", getActivationText(selectedLicense))}
                {renderDetailRow(
                  "Validate Hardware Fingerprint",
                  selectedLicense.validate_hardware_fingerprint ? "Yes" : "No"
                )}
                {renderDetailRow("Features", getFeatureBadges(selectedLicense.features))}
                {renderDetailRow("User ID", selectedLicense.user_id)}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedLicense && (
            <Button
              variant="outline-secondary"
              onClick={() => handleCopyLicenseKey(selectedLicense.license_key)}
            >
              <FaRegClipboard className="me-2" />
              Copy License Key
            </Button>
          )}
          {selectedLicense && (
            <Button
              variant="primary"
              onClick={() => {
                handleEditLicense(selectedLicense);
                setShowDetailsModal(false);
              }}
            >
              <FaEdit className="me-2" />
              Edit
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit License Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit License</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editLicense && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>License Key</Form.Label>
                <Form.Control
                  type="text"
                  value={editLicense.license_key}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Vorname</Form.Label>
                <Form.Control
                  type="text"
                  value={editLicense.first_name || ""}
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      first_name: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Nachname</Form.Label>
                <Form.Control
                  type="text"
                  value={editLicense.last_name || ""}
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      last_name: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>E-Mail-Adresse</Form.Label>
                <Form.Control
                  type="email"
                  value={editLicense.email || ""}
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      email: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Firma</Form.Label>
                <Form.Control
                  type="text"
                  value={editLicense.company || ""}
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      company: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Expiry Date</Form.Label>
                <Form.Control
                  type="date"
                  value={
                    editLicense.expires_at
                      ? new Date(editLicense.expires_at)
                        .toISOString()
                        .split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      expires_at: new Date(e.target.value).toISOString(),
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Maximum Activations</Form.Label>
                <Form.Control
                  type="number"
                  value={editLicense.max_activations || 1}
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      max_activations: parseInt(e.target.value),
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Validate Hardware Fingerprint"
                  checked={editLicense.validate_hardware_fingerprint}
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      validate_hardware_fingerprint: e.target.checked,
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Feature Flags</Form.Label>
                <Form.Check
                  type="switch"
                  id="feature-allow-builder-deploy"
                  label="Allow Builder Deploy"
                  checked={
                    editLicense.features?.allow_builder_deploy ??
                    DEFAULT_LICENSE_FEATURES.allow_builder_deploy
                  }
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      features: {
                        ...withFeatureDefaults(editLicense).features,
                        allow_builder_deploy: e.target.checked,
                      },
                    })
                  }
                />
                <Form.Text className="text-muted">
                  Enable to let this license deploy directly from the builder.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="feature-allow-tire-projects"
                  label="Allow Tire Projects"
                  className="mt-3"
                  checked={
                    editLicense.features?.allow_tire_projects ??
                    DEFAULT_LICENSE_FEATURES.allow_tire_projects
                  }
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      features: {
                        ...withFeatureDefaults(editLicense).features,
                        allow_tire_projects: e.target.checked,
                      },
                    })
                  }
                />
                <Form.Text className="text-muted">
                  Allow this license to create and open tire projects.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="feature-is-test-license"
                  label="Test License"
                  className="mt-3"
                  checked={
                    editLicense.features?.is_test_license ??
                    DEFAULT_LICENSE_FEATURES.is_test_license
                  }
                  onChange={(e) =>
                    setEditLicense({
                      ...editLicense,
                      features: {
                        ...withFeatureDefaults(editLicense).features,
                        is_test_license: e.target.checked,
                      },
                    })
                  }
                />
                <Form.Text className="text-muted">
                  Mark this license as a test license for clients that need
                  separate test behavior.
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete License Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete License</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {licenseToDelete && (
            <>
              <p>Are you sure you want to permanently delete this license?</p>
              <p>
                <strong>This action cannot be undone.</strong>
              </p>
              <p>
                License Key: <code>{licenseToDelete.license_key}</code>
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteLicense}>
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Licenses;
